/**
 * Centralized Toast Utility for TECDoc React Shell
 * 
 * Provides consistent toast notifications that match the Angular alertify styling.
 * Uses react-hot-toast with predefined themes.
 * 
 * Issue #74 - Master Layout Migration
 */

import toast from 'react-hot-toast';

// TECDoc toast theme configuration to match original TECDoc alertify exactly
const tecDocTheme = {
  success: {
    style: {
      background: '#84cc3e',  // Exact TECDoc lime green from tec-alertify.css
      color: '#fff',
      width: '100vw',
      maxWidth: 'none',
      borderRadius: 0,
      textAlign: 'center' as const,
      fontSize: '14px',
      fontWeight: 'normal',
      padding: '20px 40px',  // Exact padding from original alertify
      height: '70px',  // Exact height from original alertify
      boxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.2)',  // Exact shadow from original
      margin: 0,
      left: 0,
      right: 0,
      position: 'relative' as const,
      boxSizing: 'border-box' as const,
    },
    duration: 4000,
    position: 'top-center' as const,
  },
  error: {
    style: {
      background: '#e4311a',  // Exact TECDoc red from tec-alertify.css
      color: '#fff',
      width: '100vw',
      maxWidth: 'none',
      borderRadius: 0,
      textAlign: 'center' as const,
      fontSize: '14px',
      fontWeight: 'normal',
      padding: '20px 40px',  // Exact padding from original alertify
      height: '70px',  // Exact height from original alertify
      boxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.2)',  // Exact shadow from original
      margin: 0,
      left: 0,
      right: 0,
      position: 'relative' as const,
      boxSizing: 'border-box' as const,
    },
    duration: 4000,
    position: 'top-center' as const,
  },
  info: {
    style: {
      background: '#2ea9e6',  // Exact TECDoc blue from tec-alertify.css
      color: '#fff',
      width: '100vw',
      maxWidth: 'none',
      borderRadius: 0,
      textAlign: 'center' as const,
      fontSize: '14px',
      fontWeight: 'normal',
      padding: '20px 40px',  // Exact padding from original alertify
      height: '70px',  // Exact height from original alertify
      boxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.2)',  // Exact shadow from original
      margin: 0,
      left: 0,
      right: 0,
      position: 'relative' as const,
      boxSizing: 'border-box' as const,
    },
    duration: 4000,
    position: 'top-center' as const,
  },
};

// Centralized toast methods with TECDoc theming
export const tecDocToast = {
  /**
   * Show success toast (green banner)
   */
  success: (message: string): string => {
    return toast.success(message, tecDocTheme.success);
  },

  /**
   * Show error toast (red banner)
   */
  error: (message: string): string => {
    return toast.error(message, tecDocTheme.error);
  },

  /**
   * Show info toast (blue banner)
   */
  info: (message: string): string => {
    return toast(message, {
      ...tecDocTheme.info,
      icon: 'ℹ️',
    });
  },

  /**
   * Dismiss a specific toast
   */
  dismiss: (toastId?: string): void => {
    toast.dismiss(toastId);
  },

  /**
   * Dismiss all toasts
   */
  dismissAll: (): void => {
    toast.dismiss();
  },
};

// Export default for convenience
export default tecDocToast;